// A library shared by link with named accounts (issue 1171), as the client uses it. Every call goes to this origin
// through `requestJson` (so `authFetch`); the files themselves are fetched as a person's own are, with the bearer, and
// the server says whether the share lets them.
import type { LibraryShare, ReceivedShare, ShareItemRef } from '@dragons-table/vtt-shared';
import { requestJson } from './api';

export type ShareResult =
  | { kind: 'ok'; share: LibraryShare }
  | { kind: 'unknown_handle'; handle: string }
  | { kind: 'refused'; reason: string }
  | { kind: 'error' };

/** The owner's words for each refusal the server names. */
const REASONS: Record<string, string> = {
  cannot_share_with_self: 'That handle is yours: a share is for other people.',
  share_limit: 'You have 50 shares, the most one person keeps. Delete one first.',
  member_limit: 'A share names at most 50 people.',
  not_found: 'That share, or a file in it, is no longer there.',
  bad_request: 'A share needs a name of 1 to 120 characters.',
};

const answered = (status: number, body: unknown): ShareResult => {
  const b = body as { share?: LibraryShare; error?: string; handle?: string } | null;
  if ((status === 200 || status === 201) && b?.share) return { kind: 'ok', share: b.share };
  if (b?.error === 'unknown_handle' && typeof b.handle === 'string') return { kind: 'unknown_handle', handle: b.handle };
  if (b?.error && REASONS[b.error]) return { kind: 'refused', reason: REASONS[b.error]! };
  return { kind: 'error' };
};

/** The address a named person opens: this origin's page for the token. */
export const shareLink = (token: string, origin: string = window.location.origin): string => `${origin}/library/shared/${token}`;

export async function listMyShares(fetchImpl: typeof fetch = fetch): Promise<LibraryShare[] | null> {
  const { status, body } = await requestJson('/api/library/shares', { method: 'GET' }, fetchImpl);
  const shares = (body as { shares?: unknown } | null)?.shares;
  return status === 200 && Array.isArray(shares) ? (shares as LibraryShare[]) : null;
}

export async function createShare(share: { name: string; items: ShareItemRef[]; handles: string[] }, fetchImpl: typeof fetch = fetch): Promise<ShareResult> {
  const { status, body } = await requestJson('/api/library/shares', { method: 'POST', body: JSON.stringify(share) }, fetchImpl);
  return answered(status, body);
}

export async function updateShare(id: string, changes: { name?: string; items?: ShareItemRef[]; handles?: string[] }, fetchImpl: typeof fetch = fetch): Promise<ShareResult> {
  const { status, body } = await requestJson(`/api/library/shares/${id}`, { method: 'PUT', body: JSON.stringify(changes) }, fetchImpl);
  return answered(status, body);
}

export async function revokeMember(shareId: string, userId: string, fetchImpl: typeof fetch = fetch): Promise<ShareResult> {
  const { status, body } = await requestJson(`/api/library/shares/${shareId}/members/${userId}/revoke`, { method: 'POST' }, fetchImpl);
  return answered(status, body);
}

export async function deleteShare(id: string, fetchImpl: typeof fetch = fetch): Promise<boolean> {
  return (await requestJson(`/api/library/shares/${id}`, { method: 'DELETE' }, fetchImpl)).status === 204;
}

/** "Shared with you": the shares granted to this person. */
export async function listSharedWithMe(fetchImpl: typeof fetch = fetch): Promise<ReceivedShare[]> {
  const { status, body } = await requestJson('/api/library/shared', { method: 'GET' }, fetchImpl);
  const shares = (body as { shares?: unknown } | null)?.shares;
  return status === 200 && Array.isArray(shares) ? (shares as ReceivedShare[]) : [];
}

export type ClaimResult = { kind: 'ok'; share: ReceivedShare } | { kind: 'not_found' } | { kind: 'error' };

/** Opening a link: the share when this person is named on it; the not-found a missing link gets otherwise. */
export async function claimShare(token: string, fetchImpl: typeof fetch = fetch): Promise<ClaimResult> {
  const { status, body } = await requestJson(`/api/library/shared/${encodeURIComponent(token)}`, { method: 'POST' }, fetchImpl);
  const share = (body as { share?: ReceivedShare } | null)?.share;
  if (status === 200 && share) return { kind: 'ok', share };
  return status === 404 ? { kind: 'not_found' } : { kind: 'error' };
}
